import * as React from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
	DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
type Variant = "danger" | "success" | "warning" | "info";
import * as LucideIcon from "lucide-react"; // import all lucide icons

interface ModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title?: string;
	description?: string;
	children?: React.ReactNode;
	onConfirm?: () => void;
	onCancel?: () => void;
	confirmLabel?: string;
	cancelLabel?: string;
	iconName?: keyof typeof LucideIcon;
	variant?: Variant;
	size?: "sm" | "md" | "lg";
	className?: string;
}

export function Modal({
	open,
	onOpenChange,
	title,
	description,
	children,
	onConfirm,
	onCancel,
	confirmLabel = "Confirm",
	cancelLabel = "",
	iconName,
	variant = "danger",
	size = "sm",
	className,
}: ModalProps) {
	const [loading, setLoading] = React.useState(false);

	// eslint-disable-next-line
	const IconComponent = iconName ? (LucideIcon as any)[iconName] : null;

	// Dynamic colors
	const variantStyles: Record<
		Variant,
		{ icon: string; button: string; buttonHover: string; border: string }
	> = {
		danger: {
			icon: "text-red-600",
			button: "bg-red-600 text-white",
			buttonHover: "hover:bg-red-700",
			border: "bg-gray-200 border-gray-500 text-gray-600 hover:bg-red-50",
		},
		success: {
			icon: "text-green-600",
			button: "bg-green-600 text-white",
			buttonHover: "hover:bg-green-700",
			border: "bg-gray-200 border-gray-500 text-gray-600 hover:bg-green-50",
		},
		warning: {
			icon: "text-yellow-500",
			button: "bg-yellow-500 text-white",
			buttonHover: "hover:bg-yellow-600",
			border: "bg-gray-200 border-gray-400 text-gray-600 hover:bg-yellow-50",
		},
		info: {
			icon: "text-blue-600",
			button: "bg-blue-600 text-white",
			buttonHover: "hover:bg-blue-700",
			border: "bg-gray-200 border-gray-500 text-gray-600 hover:bg-blue-50",
		},
	};

	const colors = variantStyles[variant];

	function onConfirmClick() {
		if (onConfirm) {
			setLoading(true);
			onConfirm();
			setLoading(false);
		}
	}

	const sizeMapping = {
		sm: "sm:max-w-md",
		md: "sm:max-w-2xl",
		lg: "sm:max-w-4xl",
	};

	const width = sizeMapping[size];

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				className={`${width}  bg-gray-200 dark:bg-zinc-700 rounded-2xl shadow-lg p-0 gap-0`}
			>
				<DialogHeader className="sticky  bg-gray-200 dark:bg-zinc-700 top-0">
					{title && (
						<DialogTitle className="text-sm text-title p-4">
							{title}
						</DialogTitle>
					)}
					{description && (
						<DialogDescription className="text-gray-500">
							{description}
						</DialogDescription>
					)}
				</DialogHeader>
				<div className="bg-gray-100 dark:bg-zinc-900 px-5 rounded-b-2xl">
					{IconComponent && (
						<div className="place-self-center">
							<IconComponent className={`w-18 h-18  ${colors.icon}`} />
						</div>
					)}
					<div className={cn("py-8 text-center", className)}>{children}</div>

					<DialogFooter className="flex gap-2">
						{cancelLabel && (
							<DialogClose asChild>
								<Button
									variant="outline"
									className={colors.border}
									onClick={onCancel}
									size="sm"
								>
									{cancelLabel}
								</Button>
							</DialogClose>
						)}
						{onConfirm && (
							<Button
								className="text-sm"
								size="sm"
								onClick={onConfirmClick}
								loading={loading}
							>
								{confirmLabel}
							</Button>
						)}
					</DialogFooter>
				</div>
			</DialogContent>
		</Dialog>
	);
}
